import {BasicSymbolType} from "../../../core/reel/symbol/config/IReelSymbolStruct";
import {ArrayHelper} from "../../../core/utils/ArrayHelper";
import {IWinRatioConfig} from "../../config/base/IWinConfig";
import {IAllWinData, IConnectedWin, IWinData, stickyFS} from "./IData";

export class DataUtil {
    protected findMultSymbol(symbol: string[], windowReel: string[][]): number[][] {
        let arr: number[][] = [];

        for (let reel = 0; reel < windowReel.length; reel++) {
            arr.push([]);
            for (let row: number = 0; row < windowReel[reel].length; row++) {
                if (symbol.indexOf(windowReel[reel][row]) >= 0) {
                    arr[reel].push(row);
                }
            }
        }
        return arr;
    }

    protected createConnectedNearWinMap(
        allWin: IAllWinData,
        reels: string[][],
        evalReels: string[][],
        stickies: stickyFS[] = []
    ): boolean[][] {
        const size = reels[0].length - 2;

        const filteredReels = reels.map((r, reel) =>
            r
                .map((w, row) => {
                    if (row == 0 || size < row) return "";
                    return w;
                })
                .filter((v) => !!v)
        );

        stickies.forEach((v) => (filteredReels[v.reel][v.row] = v.type));

        let miniMap = this.minimizedConnectedWays(filteredReels, ["M", "W"], size, 2);

        if (miniMap[1].indexOf(true) >= 0) {
            for (let i = 2; i < allWin.longest; i++) {
                miniMap.push(ArrayHelper.initArrayWithValues(size, () => false));
                for (let k = 0; k < size; k++) {
                    if (allWin.reelWinPositions[i][k] && evalReels[i][k] !== "W") miniMap[i][k] = true;
                }
            }
            return miniMap;
        }
        return [];
    }

    protected minimizedConnectedWays(
        windowReel: string[][],
        specialSymbols: string[],
        reelHeight: number = 3,
        minWinLength: number = 2
    ): boolean[][] {
        let firstSymbols: string[][] = ArrayHelper.initArrayWithValues(reelHeight, (i) => [windowReel[0][i]]);
        let res: boolean[][] = ArrayHelper.initArrayWithValues(minWinLength, () =>
            ArrayHelper.initArrayWithValues(reelHeight, (i) => false)
        );

        // Lets chase after potential special connections
        for (let specIndx: number = 0; specIndx < firstSymbols.length; specIndx++) {
            if (specialSymbols.find((v) => v === firstSymbols[specIndx][0])) {
                const connected: boolean[][] = [ArrayHelper.initArrayWithValues(firstSymbols.length, (i) => i === specIndx)];

                for (let reel: number = 1; reel < windowReel.length; reel++) {
                    const connectedValues = checkConnected(connected[reel - 1], windowReel[reel], specialSymbols);
                    if (connectedValues.indexOf(!0) == -1) break;
                    connected.push(connectedValues);
                }

                if (connected.length && connected.length != windowReel.length)
                    connected.push(ArrayHelper.initArrayWithValues(reelHeight, (i) => false));

                let fetchSym: string[] = [];
                for (let conn: number = 1; conn < connected.length; conn++) {
                    connected[conn - 1].forEach((v, i) => {
                        if (v) {
                            connectCheck(i, connected[conn - 1].length, (k) => {
                                if (!connected[conn][k] && ["S", "M"].indexOf(windowReel[conn][k]) === -1) {
                                    fetchSym.push(windowReel[conn][k]);
                                }
                            });
                        }
                    });
                }
                if (fetchSym.length > 1) {
                    firstSymbols[specIndx].push(...fetchSym.filter(onlyUnique));
                } else {
                    firstSymbols[specIndx].push(...fetchSym);
                }
            }
        }

        firstSymbols.forEach((r, indx) => {
            firstSymbols[indx] = r.filter((v) => specialSymbols.indexOf(v) === -1);
        });

        for (let startIndx: number = 0; startIndx < firstSymbols.length; startIndx++) {
            for (let secDim: number = 0; secDim < firstSymbols[startIndx].length; secDim++) {
                const toCheck: string = firstSymbols[startIndx][secDim];

                const connected: boolean[][] = [ArrayHelper.initArrayWithValues(firstSymbols.length, (i) => i === startIndx)];

                //Find connections FromStart
                for (let reel: number = 1; reel < windowReel.length; reel++) {
                    const connectedValues = checkConnected(connected[reel - 1], windowReel[reel], [toCheck, ...specialSymbols]);
                    if (connectedValues.indexOf(!0) == -1) break;
                    connected.push(connectedValues);
                }

                if (
                    connected.length < minWinLength ||
                    (connected.length == minWinLength && connected[minWinLength - 1].indexOf(!0) == -1)
                ) {
                    // ignore
                    continue;
                }

                // Remove nonPossibleConnections, less than minWinLength will be removed.
                connected[minWinLength - 2].forEach((v, i) => {
                    if (v) {
                        let isPossible: boolean = false;
                        connectCheck(i, connected[minWinLength - 2].length, (checkIndx) => {
                            if (connected[minWinLength - 1][checkIndx]) isPossible = true;
                        });
                        connected[minWinLength - 2][i] = isPossible;
                    }
                });

                ArrayHelper.eachForEach(connected, (reel, row) => {
                    if (reel < 2 && connected[reel][row]) res[reel][row] = true;
                });
            }
        }

        return res;

        function onlyUnique(value: any, index: any, self: any) {
            return self.indexOf(value) === index;
        }

        function checkConnected(prevConnected: boolean[], reel: string[], symbolType: string[]): boolean[] {
            let minorMap: boolean[] = ArrayHelper.initArrayWithValues(prevConnected.length, () => false);

            prevConnected.forEach((v, i) => {
                if (v) {
                    connectCheck(i, prevConnected.length, (checkIndx) => {
                        minorMap[checkIndx] = checker(reel[checkIndx], symbolType);
                    });
                }
            });
            return minorMap;
        }

        function connectCheck(startIndx: number, maxHeight: number, func: (indx: number) => void): void {
            const startPos: number = Math.max(0, startIndx - 1); // Forward-Up
            const endPos: number = Math.min(maxHeight - 1, startIndx + 1); // Forward-Down
            for (let checkIndx: number = startPos; checkIndx <= endPos; checkIndx++) func(checkIndx);
        }

        function checker(symbol: string, target: string[]): boolean {
            return !!target.find((v) => v === symbol);
        }
    }

    protected symbolTypePos(symType: string | string[], windowReel: string[][]): number[][] {
        const res: number[][] = [];
        const isArray: boolean = !(typeof symType === "string");

        for (let r: number = 0; r < windowReel.length; r++) {
            res.push([]);
            for (let c: number = 0; c < windowReel[r].length; c++) {
                if (isArray) res[r].push(symType.indexOf(windowReel[r][c]) >= 0 ? c : -1);
                else res[r].push(windowReel[r][c] === symType ? c : -1);
            }
        }
        return res;
    }

    protected symbolBooleanPos(symType: string | string[], windowReel: string[][]): boolean[][] {
        return this.symbolTypePos(symType, windowReel).map((a) => a.map((v) => v >= 0));
    }
    

    protected fetchSymbolPos(symType: string, windowReel: string[][], singleSymPerReel?: boolean): number[][] | number[] {
        const arr: number[][] | [] = [];

        let pos: number = -1;
        for (let reel: number = 0; reel < windowReel.length; reel++) {
            if (!singleSymPerReel) arr[reel] = [];
            else pos = -1;
            for (let row: number = 0; row < windowReel[reel].length; row++) {
                if (windowReel[reel][row] == symType) {
                    if (singleSymPerReel) {
                        pos = row;

                        break;
                    } else arr[reel].push(row);
                }
            }
            if (singleSymPerReel) (arr as number[]).push(pos);
        }
        return arr;
    }

    protected totalAmountOfType(symType: string | string[], windowReel: string[][]): number {
        return this.countTypeOfEachReel(symType, windowReel).reduce((a, b) => a + b);
    }

    protected countTypeOfEachReel(symType: string | string[], windowReel: string[][]): number[] {
        let count: number = 0;
        const arr: number[] = [];
        symType = Array.isArray(symType) ? symType : [symType];
        for (let r: number = 0; r < windowReel.length; r++) {
            count = 0;
            for (let row: number = 0; row < windowReel[r].length; row++) {
                if (symType.indexOf(windowReel[r][row]) >= 0) count++;
            }
            arr.push(count);
        }
        return arr;
    }

    protected fetchHitSymbols(posArr: number[][], windowReel: string[][], filter: string[]): string[][] {
        const arr: string[][] = [];
        for (let m: number = 0; m < posArr.length; m++) {
            arr.push([]);
            for (let j: number = 0; j < posArr[m].length; j++) {
                let symTargetName: string = windowReel[m][posArr[m][j]];

                if (arr[m].indexOf(symTargetName) == -1) {
                    arr[m].push(symTargetName);
                    if ([BasicSymbolType.WILD, "WC1", "WC2", "WC3", "WC12"].indexOf(symTargetName) >= 0 && m != 0) {
                        arr[m] = arr[m].concat(arr[m]);
                    }
                }
            }
            arr[m] = arr[m].filter((sym: string) => {
                return filter.indexOf(sym) === -1;
            });
        }
        return arr;
    }

    protected countAmountOfWays(symbolSizeMap: number[][]): number {
        var res = symbolSizeMap[0].reduce((a, b) => a + b, 0);
        for (let i = 1; i < symbolSizeMap.length; i++) res *= symbolSizeMap[i].reduce((a, b) => a + b, 0);
        return res;
    }

    protected addBetLvls(data: IWinData[], betAmount: number, winRatios: IWinRatioConfig[]): IWinData[] {
        const res: IWinData[] = [];

        for (let d: number = 0; d < data.length; d++) {
            const betGrp: IWinData = <IWinData>data[d];

            let winLvl: number = 0;
            const amountRatio: number = data[d].amount / betAmount;
            winRatios.forEach((v, i) => (v.ratio <= amountRatio ? (winLvl = i) : winLvl));

            betGrp.reelWinLvlPositions = [];
            betGrp.winLvl = winLvl;
            for (let reel: number = 0; reel < data[d].reelWinPositions.length; reel++) {
                betGrp.reelWinLvlPositions[reel] = [];
                for (let row: number = 0; row < data[d].reelWinPositions[reel].length; row++) {
                    betGrp.reelWinLvlPositions[reel][row] = data[d].reelWinPositions[reel][row] ? winLvl : -1;
                }
            }

            res.push(betGrp);
        }

        res.sort((a, b) => {
            return a.amount - b.amount;
        });

        return res;
    }

    protected parseAllHits(betW: IWinData[], amountOfReels: number, reelHeights: number | number[]): IAllWinData {
        let allWinData: IAllWinData = {
            symbols: [],
            longest: 0,
            reelWinPositions: [],
            reelWinLvlPositions: [],
            totalWinCombination: 0,
            biggestWinLvl: 0,
            multiplier: 1,
        };

        for (let r: number = 0; r < amountOfReels; r++) {
            allWinData.reelWinPositions.push([]);
            allWinData.reelWinLvlPositions.push([]);
            for (let j: number = 0; j < ArrayHelper.fetchArrTypeValue(reelHeights, r); j++) {
                allWinData.reelWinPositions[r].push(false);
                allWinData.reelWinLvlPositions[r].push(-1);
            }
        }
        for (let groups = 0; groups < betW!.length; groups++) {
            const winData: IWinData = betW![groups];

            allWinData.symbols.push(winData.symbol);
            allWinData.totalWinCombination += winData.numberOfCombinations;
            allWinData.multiplier = winData.multiplier;
            if (winData.length > allWinData.longest) allWinData.longest = winData.length;

            for (let reel = 0; reel < winData.reelWinPositions.length; reel++) {
                let posRow: boolean[] = winData.reelWinPositions[reel];
                for (let row = 0; row < posRow.length; row++) {
                    if (posRow[row]) {
                        allWinData.reelWinPositions[reel][row] = posRow[row];
                        allWinData.reelWinLvlPositions[reel][row] = winData.reelWinLvlPositions[reel][row];
                        if (allWinData.biggestWinLvl < winData.reelWinLvlPositions[reel][row])
                            allWinData.biggestWinLvl = winData.reelWinLvlPositions[reel][row];
                    }
                }
            }
        }

        return allWinData;
    }

    protected createConnectedWinWays(data: IWinData[], reels: string[][]): IWinData[] {
        const gen: IConnectedWin[] = data ? [...this.generateConnectedWinWays(reels, [BasicSymbolType.WILD, "SW"])] : [];
        const res: IWinData[] = data ? this.applyWinDataForConnected(data, gen) : [];
        return res;
    }

    protected applyWinDataForConnected(data: IWinData[], gen: IConnectedWin[]): IWinData[] {
        const completedArr: IWinData[] = [];

        data.forEach((d) => {
            const fetched: IConnectedWin | undefined = gen.find((g) => g.symbol === d.symbol && g.groups[d.length]);

            if (fetched) {
                const dataToFill: IWinData = createNewBlock(fetched.symbol);

                dataToFill.amount = d.amount;
                dataToFill.length = d.length;
                dataToFill.numberOfCombinations = d.numberOfCombinations;
                dataToFill.multipliedAmount = d.multipliedAmount;
                dataToFill.multiplier = d.multiplier;
                dataToFill.reelWinPositions = convToBooleanMap(fetched.groups[d.length]);
                completedArr.push(dataToFill);
            }
        });

        function convToBooleanMap(arr: number[][]): boolean[][] {
            return ArrayHelper.initArrayWithValues(arr.length, (i) =>
                ArrayHelper.initArrayWithValues(arr[i].length, (k) => !!arr[i][k])
            );
        }

        function createNewBlock(type: string): IWinData {
            return {
                symbol: type,
                amount: 0,
                length: 0,
                winLvl: 0,
                multiplier: 0,
                multipliedAmount: 0,
                numberOfCombinations: 0,
                reelWinPositions: [],
                reelWinLvlPositions: [],
            };
        }

        return completedArr;
    }

    protected generateConnectedWinWays(windowReel: string[][], specialSymbols: string[]): IConnectedWin[] {
        const res: IConnectedWin[] = [];
        const minWinLength: number = 2;
        const maxLength: number = 5;

        let firstSymbols: string[][] = ArrayHelper.initArrayWithValues(windowReel[0].length, (i) => [windowReel[0][i]]);

        // Lets chase after potentiall special connections
        for (let specIndx: number = 0; specIndx < firstSymbols.length; specIndx++) {
            if (specialSymbols.find((v) => v === firstSymbols[specIndx][0])) {
                const connected: boolean[][] = [ArrayHelper.initArrayWithValues(firstSymbols.length, (i) => i === specIndx)];

                for (let reel: number = 1; reel < windowReel.length; reel++) {
                    const connectedValues = checkConnected(connected[reel - 1], windowReel[reel], specialSymbols);
                    if (connectedValues.indexOf(!0) == -1) break;
                    connected.push(connectedValues);
                }

                if (connected.length && connected.length != windowReel.length)
                    connected.push(ArrayHelper.initArrayWithValues(windowReel[0].length, (i) => false));

                let fetchSym: string[] = [];
                for (let conn: number = 1; conn < connected.length; conn++) {
                    connected[conn - 1].forEach((v, i) => {
                        if (v) {
                            connectCheck(i, connected[conn - 1].length, (k) => {
                                if (!connected[conn][k] && ["S", "M"].indexOf(windowReel[conn][k]) === -1) {
                                    fetchSym.push(windowReel[conn][k]);
                                }
                            });
                        }
                    });
                }
                if (fetchSym.length > 1) {
                    firstSymbols[specIndx].push(...fetchSym.filter(onlyUnique));
                } else {
                    firstSymbols[specIndx].push(...fetchSym);
                }
            }
        }

        firstSymbols.forEach((r, indx) => {
            firstSymbols[indx] = r.filter((v) => specialSymbols.indexOf(v) === -1);
        });

        for (let startIndx: number = 0; startIndx < firstSymbols.length; startIndx++) {
            for (let secDim: number = 0; secDim < firstSymbols[startIndx].length; secDim++) {
                const toCheck: string = firstSymbols[startIndx][secDim];

                const connected: boolean[][] = [ArrayHelper.initArrayWithValues(firstSymbols.length, (i) => i === startIndx)];

                //Find connections FromStart
                for (let reel: number = 1; reel < windowReel.length; reel++) {
                    const connectedValues = checkConnected(connected[reel - 1], windowReel[reel], [toCheck, ...specialSymbols]);
                    if (connectedValues.indexOf(!0) == -1) break;
                    connected.push(connectedValues);
                }

                if (connected.length < 3 || (connected.length == 3 && connected[2].indexOf(!0) == -1)) {
                    // ignore
                    continue;
                }

                // Remove nonPossibleConnections, less than minWinLength will be removed.
                connected[minWinLength - 1].forEach((v, i) => {
                    if (v) {
                        let isPossible: boolean = false;
                        connectCheck(i, connected[minWinLength - 1].length, (checkIndx) => {
                            if (connected[minWinLength][checkIndx]) isPossible = true;
                        });
                        connected[minWinLength - 1][i] = isPossible;
                    }
                });

                // Create Weighted map
                const weightM: number[][] = ArrayHelper.initArrayWithValues(connected.length, (k) =>
                    ArrayHelper.initArrayWithValues(connected[k].length, (j) => {
                        return connected[k][j] ? k + 1 : 0;
                    })
                );

                let currWinData: IConnectedWin | undefined = res.find((v) => toCheck === v.symbol);
                if (!currWinData) {
                    res.push({
                        symbol: toCheck,
                        groups: {},
                    });
                    currWinData = ArrayHelper.fetchLastValue(res);
                }

                let collectedPaths: number[][] = [];
                const prevMapping: number[][] = [];

                for (let lastIndx: number = weightM.length - 1; lastIndx >= minWinLength; lastIndx--) {
                    for (let k = 0; k < weightM[lastIndx].length; k++) {
                        const currLength: number = lastIndx + 1;
                        let isEndPoint = weightM[lastIndx][k] === currLength;

                        if (isEndPoint) {
                            if (prevMapping[lastIndx] === undefined) {
                                prevMapping[lastIndx] = ArrayHelper.initArrayWithValues(weightM[lastIndx].length, (i) =>
                                    weightM[lastIndx][i] === currLength ? currLength : 0
                                );
                                collectedPaths.push(prevMapping[lastIndx]);
                            } else {
                                if (prevMapping[lastIndx][k]) continue;
                                prevMapping[lastIndx][k] = currLength;
                                collectedPaths.push(prevMapping[lastIndx].map((v) => (v === currLength ? currLength : 0)));
                            }
                            for (let rev: number = lastIndx; rev > 0; rev--) {
                                const connectedValues = weightConnection(
                                    prevMapping[rev],
                                    weightM[rev - 1],
                                    currLength,
                                    rev + 1
                                );
                                if (prevMapping[rev - 1] == undefined) prevMapping[rev - 1] = [...connectedValues];
                                else {
                                    connectedValues.forEach((v, o) => {
                                        if (v > prevMapping[rev - 1][o]) prevMapping[rev - 1][o] = v;
                                    });
                                }
                                collectedPaths.push(connectedValues);
                            }
                        }
                    }
                }

                collectedPaths = collectedPaths.reverse();

                for (let winLength: number = minWinLength + 1; winLength <= maxLength; winLength++) {
                    mergeWeightedPos(currWinData, collectedPaths, winLength);
                }
            }
        }

        // Weight check.
        res.forEach((val) => {
            Object.keys(val.groups).forEach((key) => {
                if (key !== "5") {
                    for (let keyIndx: number = 0; keyIndx < val.groups[key].length - 1; keyIndx++) {
                        for (let rowIndx: number = 0; rowIndx < val.groups[key][keyIndx].length; rowIndx++) {
                            let isPossible: boolean = false;
                            connectCheck(rowIndx, val.groups[key][keyIndx + 1].length, (checkIndx) => {
                                if (val.groups[key][keyIndx + 1][checkIndx]) {
                                    isPossible = true;
                                }
                            });
                            if (!isPossible && val.groups[key][keyIndx][rowIndx]) {
                                val.groups[key][keyIndx][rowIndx] = 0;
                            }
                        }
                    }
                }

                for (let inc: number = windowReel.length - val.groups[key].length; inc; inc--)
                    val.groups[key].push(ArrayHelper.initArrayWithValues(windowReel[windowReel.length - inc].length, () => 0));
            });
        });

        function mergeWeightedPos(currWinData: IConnectedWin, array: number[][], lengthValue: number) {
            const filtArr: number[][] = array.filter((v) => v.indexOf(lengthValue) >= 0);
            if (!filtArr.length) return;

            if (!currWinData.groups[lengthValue]) currWinData.groups[lengthValue] = [];
            // mergeWeightedPos(currWinData.fyra.concat(collectedPaths), 4)

            currWinData.groups[lengthValue] = currWinData.groups[lengthValue].concat(filtArr);

            const template: number[][] = ArrayHelper.initArrayWithValues(lengthValue, () =>
                ArrayHelper.initArrayWithValues(array[0].length, () => 0)
            );

            ArrayHelper.eachForEach(currWinData.groups[lengthValue], (reel, row, v) => {
                if (v) template[reel % lengthValue][row] = v;
            });
            currWinData.groups[lengthValue] = template;
        }

        function weightConnection(prevConnected: number[], path: number[], highValue: number, reelValue: number): number[] {
            let minorMap: number[] = ArrayHelper.initArrayWithValues(prevConnected.length, () => 0);

            prevConnected.forEach((v, i) => {
                if (v) {
                    if (highValue === reelValue && reelValue < v) return;
                    connectCheck(i, prevConnected.length, (checkIndx) => {
                        minorMap[checkIndx] = highValue > path[checkIndx] && path[checkIndx] ? highValue : 0;
                    });
                }
            });

            return minorMap;
        }

        function onlyUnique(value: any, index: any, self: any) {
            return self.indexOf(value) === index;
        }

        function checkConnected(prevConnected: boolean[], reel: string[], symbolType: string[]): boolean[] {
            let minorMap: boolean[] = ArrayHelper.initArrayWithValues(prevConnected.length, () => false);

            prevConnected.forEach((v, i) => {
                if (v) {
                    connectCheck(i, prevConnected.length, (checkIndx) => {
                        minorMap[checkIndx] = checker(reel[checkIndx], symbolType);
                    });
                }
            });
            return minorMap;
        }

        function connectCheck(startIndx: number, maxHeight: number, func: (indx: number) => void): void {
            const startPos: number = Math.max(0, startIndx - 1); // Forward-Up
            const endPos: number = Math.min(maxHeight - 1, startIndx + 1); // Forward-Down
            for (let checkIndx: number = startPos; checkIndx <= endPos; checkIndx++) func(checkIndx);
        }

        function checker(symbol: string, target: string[]): boolean {
            return !!target.find((v) => v === symbol);
        }

        return res;
    }

    protected createBetWaysWins(
        reels: string[][],
        serverBetWindata: IWinData[],
        fromLeft: boolean = true,
        fromRight: boolean = false
    ): IWinData[] {
        const res: IWinData[] = [];

        if (!serverBetWindata.length) return res;

        if (fromLeft) res.push(...this.generateBetWinWays(serverBetWindata, reels));
        if (fromRight) res.push(...this.generateBetWinWays(serverBetWindata, reels.reverse()).reverse());

        return res;
    }

    protected generateBetWinWays(serverBetWindata: IWinData[], windowReel: string[][]): IWinData[] {
        const res: IWinData[] = [];

        for (let i: number = 0; i < serverBetWindata.length; i++) {
            const serverData: IWinData = serverBetWindata[i];
            const targetSymbol: string = serverData.symbol;
            const reelPos: boolean[][] = ArrayHelper.initArrayWithValues(windowReel.length, (r) => {
                if (serverData.length > r) return reelHitBoolPos(windowReel[r], [targetSymbol, BasicSymbolType.WILD]);
                else return ArrayHelper.initArrayWithValues(windowReel[r].length, () => false);
            });
            res.push({
                symbol: targetSymbol,

                length: serverData.length,
                reelWinPositions: reelPos,

                amount: serverData.amount,
                numberOfCombinations: serverData.numberOfCombinations,

                multiplier: serverData.multiplier,
                multipliedAmount: serverData.multipliedAmount,

                winLvl: undefined!,
                reelWinLvlPositions: undefined!,
            });
        }

        function reelHitBoolPos(reel: string[], symbolType: string[]): boolean[] {
            return reel.map((val: string) => {
                return checker(val, symbolType);
            });
        }

        function checker(symbol: string, target: string[]): boolean {
            return !!target.find((v) => v === symbol);
        }

        return res;
    }

    protected getWinRatioConfig(winRatio: number, allWinRatios: IWinRatioConfig[]): IWinRatioConfig {
        for (let i: number = allWinRatios.length - 1; i >= 0; i--) {
            const winLevel = allWinRatios[i];
            if (winRatio >= winLevel.ratio) {
                return winLevel;
            }
        }
        throw "Could'nt find WinRatio config!";
    }
}
